/* eslint-disable @typescript-eslint/no-unsafe-member-access */
import { Component, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { Account, Customer } from '@inyova/models';
import { is3aAccount } from '@inyova/utils';

import { select, Store } from '@ngrx/store';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import * as StrategyActions from '@strategy/strategy.actions';
import * as fromAppRoot from '@app/app.reducers';
import * as fromStrategy from '@strategy/reducers';

import { MAXIMUM_3A_CONTRIBUTION } from '@app/app.constants';
import { AccountOpeningService } from '@shared/services/account-opening.service';
import { LookUpService } from '@shared/services/lookup.service';
import { TrackingService } from '@shared/services/tracking.service';

@Component({
  selector: 'app-fund',
  templateUrl: './fund.component.html',
  styleUrls: ['./fund.component.scss'],
  standalone: false
})
export class FundComponent implements OnInit, OnDestroy {
  currentStepStatus = 1;
  iban: string;
  idnowCompleted: boolean;
  accountOpened: boolean;
  accountFunded: boolean;
  currency: string;
  oneTimeInvested = 0;
  monthlyInvested = 0;
  isKidAccount: boolean;
  customerData: Customer;
  currentAccount: Partial<Account>;
  currentAccountType: string;
  maximumContributionLevel: string;
  maximumContributionAmount: number;
  isTransfer3AOnly: boolean;
  currentYear: number;
  showUnemployed3ANote = false;
  private readonly onDestroy$ = new Subject<void>();

  constructor(
    public router: Router,
    private lookUpService: LookUpService,
    private accountOpeningService: AccountOpeningService,
    private trackingService: TrackingService,
    private store: Store
  ) {}

  ngOnInit(): void {
    this.store.pipe(select(fromAppRoot.selectCustomer), takeUntil(this.onDestroy$)).subscribe((customer) => {
      this.customerData = customer;
    });

    this.store.pipe(select(fromAppRoot.selectCurrentAccount), takeUntil(this.onDestroy$)).subscribe((value) => {
      if (!value) {
        return;
      }

      this.currentAccount = value;
      this.currentStepStatus = value.step_status;
      this.currency = value.currency;
      this.iban = this.spaceIban(value.iban);
      this.maximumContributionLevel = is3aAccount(value) && value.max_contribution_level;
      this.currentYear = new Date().getFullYear();
      this.maximumContributionAmount = MAXIMUM_3A_CONTRIBUTION[this.currentYear][this.maximumContributionLevel];
      this.idnowCompleted = value.customer_journey.id_verification_completed;
      this.accountOpened = value.step_status >= 4;
      if (value.kind === '3a') {
        this.checkEmploymentDetail();
      }
      this.trackingService.trackView(this.accountOpened ? '/fund/opened' : '/fund/opening');
      this.currentAccountType = value.kind;
      this.accountFunded = value.customer_journey.account_funded || false;
      this.isTransfer3AOnly = this.maximumContributionLevel === 'transfer_only';
      this.isKidAccount = value.kind === 'kid';

      if (this.isKidAccount === true) {
        this.trackingService.trackActivity('Funding (kids) page visited', true);
      }

      if (this.currentStepStatus < 3 || (this.currentStepStatus === 3 && this.idnowCompleted === false && this.currentAccountType === '3b')) {
        // in case that customer is in step lower then 3 or if in step 3 but didn't finish IDnow
        void this.router.navigate(['/']);
      }

      // Redirect to reports if customer tries to go to next stes when accoutn is already funded
      if ((this.currentStepStatus >= 4 && this.idnowCompleted && this.accountFunded) || this.currentStepStatus === 5) {
        // need to check step 5 because of old customers
        void this.router.navigate(['/reports']);
      }
    });

    if (this.currentAccount.kind === '3b') {
      this.store.dispatch(StrategyActions.getCustomerInvestment());
    }

    this.store.pipe(select(fromStrategy.selectCustomerInvestmentData), takeUntil(this.onDestroy$)).subscribe((response) => {
      this.oneTimeInvested = response.total;
      this.monthlyInvested = response.monthly_investment;
    });

    // eslint-disable-next-line no-useless-concat
    const fbq_almost_customer = document.cookie.match(RegExp('(?:^|;\\s*)' + 'fbq_almost_customer' + '=([^;]*)'));
    if (fbq_almost_customer === null) {
      const expDate = 'Tue, 26 Apr 2026 08:00:00 GMT';
      document.cookie = `fbq_almost_customer=yes;expires=${expDate}`;

      const prefix = this.currentAccount.kind === '3a' ? '3a' : this.customerData.app_location;
    }
  }

  spaceIban(iban: string): string {
    if (!iban) return;
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    return iban
      .replace(/[^\dA-Z]/g, '')
      .replace(/(.{4})/g, '$1 ')
      .trim();
  }

  adaptSum(): void {
    this.trackingService.trackActivity('FundingSummaryCTA: Adapt sum', true);
    setTimeout(() => {
      this.store.dispatch(StrategyActions.setTabIndex({ tabIndex: 2 }));
    });
    void this.router.navigate(['/strategy']);
  }

  onOpenStrategy(adaptSum = false) {
    if (adaptSum) {
      this.trackingService.trackActivity('FundingSummaryCTA: Change investment sum', true);
      this.adaptSum();
      return;
    }
    this.trackingService.trackActivity('FundingSummaryCTA: Check strategy', true);
    void this.router.navigate(['/strategy']);
  }

  ngOnDestroy(): void {
    this.onDestroy$.next();
    this.onDestroy$.complete();
  }

  // Liberty requirement to check employment detail
  private checkEmploymentDetail() {
    this.accountOpeningService.getEmploymentDetail().subscribe((response) => {
      this.lookUpService.get3AEmploymentStatusList().subscribe((employmentIDs) => {
        const unemployed = employmentIDs.find((item) => item.inyova_id === 259);
        const unemployedWithBenefits = employmentIDs.find((item) => item.inyova_id === 263);
        if (unemployed && response.employment_detail.employment_id === unemployed.id) {
          this.showUnemployed3ANote = response.employment_detail.income_subject_ahv && !response.employment_detail.pension_fund;
        }
        if (unemployedWithBenefits && response.employment_detail.employment_id === unemployedWithBenefits.id) {
          this.showUnemployed3ANote = !response.employment_detail.income_subject_ahv && !response.employment_detail.pension_fund;
        }
      });
    });
  }
}
